import {
    AccrualLineSnapshot,
    computeAccrualLineDeltas,
    toAccrualLineArray,
} from "@common/helpers/accrualLineDeltas";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import {
    HISTORY_FIELD_LABELS,
    HistoryField,
} from "@common/constants/history-source-types";
import { getAccrualLineFieldLabel } from "../../../auditLog/accrual/lineFieldLabels";
import { isEmptySnapshotValue } from "./isEmptySnapshotValue";

export function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

export function isHistoryField(value: string): value is HistoryField {
    return value in HISTORY_FIELD_LABELS;
}

export function getHistoryFieldLabel(field: string): string {
    if (!isHistoryField(field)) {
        return field;
    }
    return HISTORY_FIELD_LABELS[field];
}

export function getPreferredSnapshot(
    log: HistoryDetailsItem,
): Record<string, unknown> | undefined {
    if (!isRecord(log.metadata)) {
        return undefined;
    }
    const before = isRecord(log.metadata.beforeSnapshot)
        ? log.metadata.beforeSnapshot
        : undefined;
    const after = isRecord(log.metadata.afterSnapshot)
        ? log.metadata.afterSnapshot
        : undefined;
    if (log.changeType === AuditLogChangeType.DELETE) {
        return before ?? after;
    }
    return after ?? before;
}
import {
    filterDisplayableDiffs,
    HistoryTransactionDetailsRow,
} from "../transactions/views/historyTransactionDetailsViewConfig";
import { HistoryRuleGroupUpdateDetailsRow } from "../ruleGroups/views/historyRuleGroupDetailsViewConfig";

export const hasDisplayableChanges = (log: HistoryDetailsItem): boolean => {
    if (log.changeType === AuditLogChangeType.CREATE) {
        return true;
    }
    if (log.changeType === AuditLogChangeType.DELETE) {
        return true;
    }
    return filterDisplayableDiffs(log.diff).length > 0;
};

/**
 * Removes category (`categoryId`) diffs. GL-first workspaces hide category
 * changes on the Activity page, so when a kept (mixed) log is rendered in a
 * detail drawer its category portion is dropped while other fields remain.
 */
export const excludeCategoryDiffs = (diffs: AuditLogDiff[]): AuditLogDiff[] =>
    diffs.filter((diff) => diff.field !== HistoryField.CATEGORY_ID);

export function formatAuditLogRefValue(
    ref: AuditLogDiff["before"] | AuditLogDiff["after"],
): string {
    if (ref.valueLabel) {
        return ref.valueLabel;
    }
    const v = ref.value ?? null;
    if (v === null || v === undefined) {
        return "-";
    }
    if (typeof v === "boolean") {
        return v ? "yes" : "no";
    }
    if (typeof v === "string" || typeof v === "number") {
        return String(v);
    }
    return JSON.stringify(v);
}

export function getRuleGroupDisplayName(log: HistoryDetailsItem): string {
    const fromResource = (log.resource as { name?: string } | null)?.name;
    if (typeof fromResource === "string" && fromResource.length > 0) {
        return fromResource;
    }
    const nameDiff = log.diff.find((d) => d.field === "name");
    if (nameDiff) {
        if (log.changeType === AuditLogChangeType.DELETE) {
            const v = formatAuditLogRefValue(nameDiff.before);
            if (v !== "-") {
                return v;
            }
        } else {
            const v = formatAuditLogRefValue(nameDiff.after);
            if (v !== "-") {
                return v;
            }
        }
    }
    return "Rule group";
}

function getRuleGroupNameFromResource(
    log: HistoryDetailsItem,
): string | undefined {
    const resourceGroup = log.resource?.group as
        | Record<string, unknown>
        | undefined;
    const resourceName = resourceGroup?.name;
    return typeof resourceName === "string" && resourceName.length > 0
        ? resourceName
        : undefined;
}

function getRuleGroupNameFromMetadata(
    log: HistoryDetailsItem,
): string | undefined {
    const metadata = log.metadata as Record<string, unknown> | undefined;
    const beforeSnapshot = metadata?.beforeSnapshot as
        | Record<string, unknown>
        | undefined;
    const afterSnapshot = metadata?.afterSnapshot as
        | Record<string, unknown>
        | undefined;
    const preferredSnapshot =
        log.changeType === AuditLogChangeType.DELETE
            ? (beforeSnapshot ?? afterSnapshot)
            : (afterSnapshot ?? beforeSnapshot);
    const snapshotGroup = preferredSnapshot?.group as
        | Record<string, unknown>
        | undefined;
    const snapshotName = snapshotGroup?.name;
    return typeof snapshotName === "string" && snapshotName.length > 0
        ? snapshotName
        : undefined;
}

function getRuleGroupNameFromDiff(log: HistoryDetailsItem): string | undefined {
    const groupDiff = log.diff.find((d) => d.field === "groupId");
    if (!groupDiff) {
        return undefined;
    }
    const preferredRef =
        log.changeType === AuditLogChangeType.DELETE
            ? groupDiff.before
            : groupDiff.after;
    return typeof preferredRef.valueLabel === "string" &&
        preferredRef.valueLabel.length > 0
        ? preferredRef.valueLabel
        : undefined;
}

export function resolveRuleGroupNameForLogs(
    logs: HistoryDetailsItem[],
): string {
    for (const log of logs) {
        const fromResource = getRuleGroupNameFromResource(log);
        if (fromResource) {
            return fromResource;
        }

        const fromMetadata = getRuleGroupNameFromMetadata(log);
        if (fromMetadata) {
            return fromMetadata;
        }

        const fromDiff = getRuleGroupNameFromDiff(log);
        if (fromDiff) {
            return fromDiff;
        }
    }

    return "No group";
}

export function buildRuleGroupUpdateDetailsRow(
    log: HistoryDetailsItem,
): HistoryRuleGroupUpdateDetailsRow {
    const groupName = getRuleGroupDisplayName(log);
    const base = {
        id: log.id,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        groupName,
        logDate: log.createdAt,
        date: log.createdAt,
        entityId: log.entityId ?? null,
    };
    if (log.changeType === AuditLogChangeType.CREATE) {
        return {
            ...base,
            changedFieldLabel: null,
            previous: "-",
            modified: groupName,
        };
    }
    if (log.changeType === AuditLogChangeType.DELETE) {
        return {
            ...base,
            changedFieldLabel: null,
            previous: groupName,
            modified: "-",
        };
    }
    const nameDiff = log.diff.find((d) => d.field === "name");
    const onlyName =
        log.diff.length > 0 && log.diff.every((d) => d.field === "name");
    if (onlyName && nameDiff) {
        return {
            ...base,
            changedFieldLabel: "name",
            previous: formatAuditLogRefValue(nameDiff.before),
            modified: formatAuditLogRefValue(nameDiff.after),
        };
    }
    return {
        ...base,
        changedFieldLabel: null,
        previous: log.diff
            .map((d) => `${d.field}: ${formatAuditLogRefValue(d.before)}`)
            .join(" · "),
        modified: log.diff
            .map((d) => `${d.field}: ${formatAuditLogRefValue(d.after)}`)
            .join(" · "),
    };
}

function lineIdentity(
    line: AccrualLineSnapshot,
    fallbackIndex: number,
): string {
    if (line.description && line.description.trim().length > 0) {
        return `line "${line.description}"`;
    }
    return `line ${fallbackIndex + 1}`;
}

const SNAPSHOT_FIELDS: Array<{ field: string; label: string }> = [
    { field: "status", label: "status" },
    { field: "invoiceNumber", label: "number" },
    { field: "counterpartyId", label: "counterparty" },
    { field: "total", label: "total" },
    { field: "subtotal", label: "subtotal" },
    { field: "tax", label: "tax" },
    { field: "discount", label: "discount" },
    { field: "currency", label: "currency" },
    { field: "due", label: "due" },
    { field: "paidAt", label: "paid" },
    { field: "uncollectableAt", label: "uncollectible" },
    { field: "voidedAt", label: "void" },
    { field: "description", label: "description" },
    { field: "memo", label: "memo" },
    { field: "isSubscription", label: "subscription" },
];

function formatSnapshotValue(refValue: AuditLogDiff["after"]): string | null {
    if (isEmptySnapshotValue(refValue)) {
        return null;
    }
    if (refValue.valueLabel) {
        return refValue.valueLabel;
    }
    const value = refValue.value;
    if (typeof value === "boolean") {
        return value ? "yes" : "no";
    }
    if (typeof value === "string" || typeof value === "number") {
        return String(value);
    }
    return null;
}

const TRANSACTION_SNAPSHOT_FIELDS: Array<{ field: string; label: string }> = [
    { field: "displayDescription", label: "description" },
    { field: "bankDescription", label: "bank description" },
    { field: "counterpartyId", label: "counterparty" },
    { field: "categoryId", label: "category" },
    { field: "memo", label: "memo" },
];

export function buildTransactionSnapshot(
    diff: AuditLogDiff[],
    side: "before" | "after",
): string {
    const parts: string[] = [];
    const diffByField = new Map<string, AuditLogDiff>();
    for (const entry of diff) {
        diffByField.set(entry.field, entry);
    }
    for (const { field, label } of TRANSACTION_SNAPSHOT_FIELDS) {
        const entry = diffByField.get(field);
        if (!entry) {
            continue;
        }
        const formatted = formatSnapshotValue(entry[side]);
        if (formatted === null) {
            continue;
        }
        parts.push(`${label}: ${formatted}`);
        if (parts.length >= 3) {
            break;
        }
    }
    return parts.join(" • ");
}

export function buildDiffSideSnapshotMap(
    diff: AuditLogDiff[],
    side: "before" | "after",
): Record<string, unknown> {
    const snapshot: Record<string, unknown> = {};
    for (const entry of diff) {
        snapshot[entry.field] = entry[side].value;
    }
    return snapshot;
}

export function buildAccrualSnapshot(
    diff: AuditLogDiff[],
    side: "before" | "after",
): string {
    const parts: string[] = [];
    const diffByField = new Map<string, AuditLogDiff>();
    for (const entry of diff) {
        diffByField.set(entry.field, entry);
    }
    for (const { field, label } of SNAPSHOT_FIELDS) {
        const entry = diffByField.get(field);
        if (!entry) {
            continue;
        }
        const formatted = formatSnapshotValue(entry[side]);
        if (formatted === null) {
            continue;
        }
        parts.push(`${label}: ${formatted}`);
    }
    const linesEntry = diffByField.get("lines");
    if (linesEntry) {
        const value = linesEntry[side].value;
        if (Array.isArray(value) && value.length > 0) {
            parts.push(pluralizeWithCount("line", value.length));
        }
    }
    return parts.join(" • ");
}

export interface AccrualRowContext {
    log: HistoryDetailsItem;
    resolvedDate: Date;
    resolvedAmount: number;
}

export function buildAccrualLineRows(
    diff: AuditLogDiff,
    context: AccrualRowContext,
): HistoryTransactionDetailsRow[] {
    const { log, resolvedDate, resolvedAmount } = context;
    const beforeLines = toAccrualLineArray(diff.before.value);
    const afterLines = toAccrualLineArray(diff.after.value);
    const deltas = computeAccrualLineDeltas(beforeLines, afterLines);
    const rows: HistoryTransactionDetailsRow[] = [];
    const entityId = log.resource?.entityId ?? log.entityId;
    const financialAccountId = log.resource?.financialAccountId;
    const baseRow = {
        logDate: log.createdAt,
        date: resolvedDate,
        changeType: log.changeType,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        resource: log.resource,
        financialAccountId,
        amount: resolvedAmount,
        entityId,
    };

    deltas.forEach((delta, deltaIndex) => {
        const lineDesc = lineIdentity(delta.line, deltaIndex);
        if (delta.type === "added") {
            rows.push({
                ...baseRow,
                id: `${log.id}:${delta.id}:added`,
                field: "lines",
                fieldLabel: "Added",
                subDescription: `Added ${lineDesc}`,
                oldValue: { value: null, valueLabel: undefined },
                newValue: {
                    value:
                        `${delta.line.description ?? ""}`.trim() ||
                        "(new line)",
                    valueLabel: undefined,
                },
            });
            return;
        }
        if (delta.type === "removed") {
            rows.push({
                ...baseRow,
                id: `${log.id}:${delta.id}:removed`,
                field: "lines",
                fieldLabel: "Removed",
                subDescription: `Removed ${lineDesc}`,
                oldValue: {
                    value:
                        `${delta.line.description ?? ""}`.trim() ||
                        "(removed line)",
                    valueLabel: undefined,
                },
                newValue: { value: null, valueLabel: undefined },
            });
            return;
        }
        for (const change of delta.fieldChanges ?? []) {
            const label = getAccrualLineFieldLabel(change.field);
            rows.push({
                ...baseRow,
                id: `${log.id}:${delta.id}:${change.field}`,
                field: change.field,
                fieldLabel: label,
                subDescription: `${lineDesc} — ${label}`,
                oldValue: { value: change.before, valueLabel: undefined },
                newValue: { value: change.after, valueLabel: undefined },
            });
        }
    });

    return rows;
}
